import React from 'react';
import { Link } from 'react-router-dom';
import { useCMS } from '../context/CMSContext';
import SEO from '../components/SEO';
import Gallery from '../components/Gallery';

export default function ServiceChiropractic() {
  const { getPage } = useCMS();
  const page = getPage('kiropraktik');

  if (!page) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="h-8 w-48 bg-brand-sand/40 rounded" />
          <div className="h-4 w-64 bg-brand-sand/30 rounded" />
        </div>
      </div>
    );
  }

  const { title, content, meta_title, meta_description } = page;

  // Hjælper til Cloudflare on-the-fly resizing af billeder
  const getResizedUrl = (url, width = 1200, quality = 80) => {
    return url.replace(
      'https://assets.auxodev.dk/assets/',
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/assets/`
    );
  };

  const mainImage = 'https://assets.auxodev.dk/assets/19a97e0e-8e3c-4b11-bc4c-e5bcc52ff735/equine-portrait.jpg';

  return (
    <>
      <SEO 
        title={title?.da || 'Kiropraktik til Heste'} 
        description={meta_description?.da} 
      />

      {/* Header Sektion */}
      <section className="bg-brand-beige/30 border-b border-brand-sand/30 py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-brown mb-3 block">
            Ydelser &bull; Heste
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-brand-dark">
            {title?.da}
          </h1>
          <p className="text-brand-brown text-sm md:text-base mt-4 max-w-xl mx-auto">
            Genskab din hests naturlige bevægelighed, balance og præstationsevne med nænsom og faglig kiropraktik.
          </p>
        </div>
      </section>

      {/* Indhold Sektion */}
      <section className="py-16 md:py-24 bg-brand-cream">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Tekstindhold */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div 
                className="prose prose-stone prose-headings:font-serif prose-headings:font-bold prose-headings:text-brand-dark prose-p:text-brand-brown prose-p:leading-relaxed prose-li:text-brand-brown prose-strong:text-brand-dark max-w-none"
                dangerouslySetInnerHTML={{ __html: content?.da || '' }}
              />
              
              <div className="mt-6 p-6 rounded-brand bg-brand-beige/40 border border-brand-sand/40">
                <h3 className="font-serif text-lg font-bold text-brand-dark mb-3">
                  Hvornår har min hest brug for kiropraktik?
                </h3>
                <p className="text-brand-brown text-sm leading-relaxed mb-4">
                  Heste kan ikke fortælle os, når de har ondt, men de viser det ofte gennem ændret adfærd eller nedsat præstation. Hold øje med følgende tegn:
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs text-brand-brown font-medium">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/60 text-[10px]" /> Uvilje mod at stille eller bøje sig
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/60 text-[10px]" /> Taktfejl eller uforklarlig nedsat præstation
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/60 text-[10px]" /> Modstand mod sadling eller berøring
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/60 text-[10px]" /> Problemer med galopanspring eller changementer
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/60 text-[10px]" /> Hovedrysten (headshaking) eller piskende hale
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-brand-brown/60 text-[10px]" /> Asymmetrisk muskeludvikling
                  </li>
                </ul>
              </div>
            </div>

            {/* Billede og Info-boks */}
            <div className="lg:col-span-5 flex flex-col gap-8 lg:sticky lg:top-24">
              <div className="aspect-[4/3] sm:aspect-[16/10] lg:aspect-[4/3] rounded-brand overflow-hidden border border-brand-sand/60 shadow-lg bg-brand-sand/20">
                <img
                  src={getResizedUrl(mainImage, 800, 85)}
                  alt="Rolig hest i velholdt staldmiljø hos EquiGramVet"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>

              <div className="p-6 rounded-brand border border-brand-sand/50 bg-brand-cream shadow-sm">
                <h4 className="font-serif text-base font-bold text-brand-dark mb-3">
                  Behandlingsforløb
                </h4>
                <div className="flex flex-col gap-4 text-xs text-brand-brown">
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-brand-beige flex items-center justify-center text-brand-dark font-bold shrink-0">1</div>
                    <p className="leading-relaxed"><strong>Grundig journal og samtale:</strong> Vi starter med en snak om hestens historik, træning og eventuelle problemer.</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-brand-beige flex items-center justify-center text-brand-dark font-bold shrink-0">2</div>
                    <p className="leading-relaxed"><strong>Bevægelsesanalyse:</strong> Hesten observeres i bevægelse (mønstring og evt. longe) for at vurdere dens biomekanik.</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-brand-beige flex items-center justify-center text-brand-dark font-bold shrink-0">3</div>
                    <p className="leading-relaxed"><strong>Palpering og behandling:</strong> Led og muskler mærkes igennem, og eventuelle låsninger korrigeres med præcise, blide greb.</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-brand bg-brand-beige/50 border border-brand-sand/60 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-brand-brown">Særtilbud resten af 2026</span>
                  <span className="font-serif font-bold text-lg text-brand-dark">Fra 899.-</span>
                </div>
                <p className="text-xs text-brand-brown leading-relaxed">
                  Første behandling af hest 899.- (20% rabat ved efterfølgende hest i samme stald). Opfølgende 499.-. Inkl. moms.
                </p>
                <Link
                  to="/priser"
                  className="mt-1 text-center py-2 px-4 bg-brand-dark text-brand-cream text-xs font-medium rounded-brand hover:bg-brand-brown transition-colors"
                >
                  Se alle priser og betingelser
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Galleri Sektion */}
      <section className="py-16 md:py-20 bg-brand-beige/20 border-t border-brand-sand/30">
        <div className="mx-auto max-w-7xl px-6">
          <Gallery gallerySlug="kiropraktik-heste" />
        </div>
      </section>
    </>
  );
}
